/* One controller per section; one request per unloaded tab, including rapid repeated clicks. */
(() => {
  if (window.FeaturedCollectionTabs) return;
  const sections = new WeakSet();
  const requests = new WeakMap();
  const rowSizes = new WeakMap();
  function retainRowHeight(block) {
    if (!block) return;
    const grids = Array.from(block.querySelectorAll('.products-load'));
    const width = block.getBoundingClientRect().width;
    let size = rowSizes.get(block);
    // Re-measure at a new width instead of carrying a mobile row height onto desktop.
    if (!size || size.width !== width) {
      grids.forEach(grid => grid.style.removeProperty('min-height'));
      size = { width, height: 0 };
    }
    const active = block.querySelector('.tab-active[data-product-TabContent] .products-load');
    size.height = Math.max(size.height, active?.getBoundingClientRect().height || 0);
    if (size.height) grids.forEach(grid => grid.style.minHeight = size.height + 'px');
    rowSizes.set(block, size);
  }
  function placeholder(target, show) {
    const grid = target.querySelector('.products-load');
    if (!grid) return;
    if (show && !grid.children.length) {
      const template = document.getElementById('product-tab-placeholder-template');
      if (template) grid.appendChild(template.content.cloneNode(true));
    }
    if (!show) grid.querySelectorAll('[data-tab-skeleton]').forEach(card => card.remove());
  }
  function load(tab, target, visible) {
    if (target.classList.contains('prods-loaded')) return Promise.resolve();
    if (visible) { placeholder(target, true); target.setAttribute('aria-busy', 'true'); }
    if (requests.has(target)) return requests.get(target);
    const request = fetch(tab.dataset.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(response => {
        if (!response.ok) throw new Error('Collection request failed');
        return response.text();
      })
      .then(markup => {
        // Discard responses for sections replaced in the editor.
        if (!target.isConnected) return;
        target.querySelector('.products-load').innerHTML = markup;
        target.classList.add('prods-loaded');
        window.initThemeSliders?.(target);
        target.querySelector('.swiper-container-api')?.swiper?.update();
        retainRowHeight(target.closest('[data-product-tab-block]'));
        window.dispatchEvent(new CustomEvent('featuredCollectionTabChange'));
      })
      .catch(error => {
        // Background failures remain retryable and never replace a populated tab.
        if (target.classList.contains('tab-active')) {
          const grid = target.querySelector('.products-load');
          placeholder(target, false);
          if (!grid.children.length) grid.textContent = 'An error occurred while loading data.';
        }
        console.error('Unable to load collection tab:', error.message);
      })
      .finally(() => {
        placeholder(target, false); target.removeAttribute('aria-busy'); requests.delete(target);
      });
    requests.set(target, request);
    return request;
  }
  function init(root = document) {
    const blocks = [ ...(root.matches?.('[data-product-tab-block]') ? [root] : []), ...root.querySelectorAll('[data-product-tab-block]') ];
    blocks.forEach(block => {
      if (sections.has(block)) return;
      sections.add(block);
      const tabs = Array.from(block.querySelectorAll('[data-product-tabTop]'));
      const contents = Array.from(block.querySelectorAll('[data-product-TabContent]'));
      retainRowHeight(block);
      tabs.forEach(tab => {
        const target = contents.find(content => '#' + content.id === tab.dataset.target);
        if (!target) return;
        const warm = () => load(tab, target, false);
        tab.addEventListener('pointerenter', warm, { passive: true });
        tab.addEventListener('focusin', warm);
        tab.addEventListener('pointerdown', warm, { passive: true });
        tab.addEventListener('click', event => {
          event.preventDefault();
          retainRowHeight(block);
          tabs.forEach(item => item.classList.toggle('tab-active', item === tab));
          contents.forEach(item => item.classList.toggle('tab-active', item === target));
          load(tab, target, true);
          window.initThemeSliders?.(target, true);
          target.querySelector('.swiper-container-api')?.swiper?.update();
          retainRowHeight(block);
        });
      });
    });
  }
  window.FeaturedCollectionTabs = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  let resizeFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => document.querySelectorAll('[data-product-tab-block]').forEach(retainRowHeight));
  }, { passive: true });
})();
